/**
 * ClaudeUsage — Token usage card for the Mission Control dashboard.
 * Reads from /api/claude-usage which parses ~/.claude/stats-cache.json.
 */
'use client';

import React from 'react';
import { Cpu } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';
import type { ClaudeUsageData } from '@/lib/types';

function formatTokens(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

function formatCost(n: number): string {
  return '$' + n.toFixed(2);
}

const EMPTY: ClaudeUsageData = {
  summary: { totalSessions: 0, totalMessages: 0, firstSessionDate: '', lastComputedDate: '' },
  tokenBreakdown: { input: 0, output: 0, cacheRead: 0, cacheCreate: 0 },
  dailyActivity: [],
  totalEstimatedCostUSD: 0,
  noCacheCostUSD: 0,
  totalToolCalls: 0,
};

const TOKEN_ROWS = [
  { key: 'input' as const, label: 'INPUT', color: 'bg-emerald-400' },
  { key: 'output' as const, label: 'OUTPUT', color: 'bg-amber-400' },
  { key: 'cacheRead' as const, label: 'CACHE READ', color: 'bg-cyan-400' },
  { key: 'cacheCreate' as const, label: 'CACHE CREATE', color: 'bg-blue-400' },
];

export const ClaudeUsage = () => {
  const { data, isLoading } = usePolling<ClaudeUsageData>({
    url: '/api/claude-usage',
    intervalMs: 60000,
    initialValue: EMPTY,
  });

  const dailyActivity = data?.dailyActivity ?? [];
  const maxMessages = dailyActivity.length > 0 ? Math.max(...dailyActivity.map(d => d.messages), 1) : 1;
  const dayCount = dailyActivity.length;

  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Cpu className="w-4 h-4 text-zinc-500" />
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Claude Code Usage</p>
        </div>
        {dayCount > 0 && (
          <span className="text-[10px] text-zinc-400 font-mono">{dayCount}D</span>
        )}
      </div>

      {isLoading ? (
        <div className="text-zinc-500 text-[10px] font-mono text-center py-4">Loading...</div>
      ) : (
        <>
          {/* Hero cost metric */}
          <div className="mb-5">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-2xl font-black text-white tracking-tighter tabular-nums">
                {formatCost(data?.totalEstimatedCostUSD ?? 0)}
              </span>
              {(data?.noCacheCostUSD ?? 0) > 0 && (
                <span className="text-[10px] text-zinc-400 font-mono line-through tabular-nums">
                  {formatCost(data?.noCacheCostUSD ?? 0)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-500 font-mono uppercase">Estimated Spend</span>
              {(data?.noCacheCostUSD ?? 0) > 0 && (data?.totalEstimatedCostUSD ?? 0) > 0 && (
                <span className="text-[10px] text-emerald-500 font-mono tabular-nums">
                  {Math.round((1 - (data?.totalEstimatedCostUSD ?? 0) / (data?.noCacheCostUSD ?? 1)) * 100)}% saved
                </span>
              )}
            </div>
          </div>

          {/* Token breakdown */}
          <div className="space-y-2 mb-5">
            {TOKEN_ROWS.map(row => (
              <div key={row.key} className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${row.color}`} />
                  <span className="text-zinc-400 font-mono text-[10px]">{row.label}</span>
                </div>
                <span className="text-white font-mono text-xs tabular-nums">
                  {formatTokens(data?.tokenBreakdown?.[row.key] ?? 0)}
                </span>
              </div>
            ))}
          </div>

          {/* Daily activity sparkline */}
          {dailyActivity.length > 0 && (
            <div className="mb-5 pt-4 border-t border-white/5">
              <div className="text-[10px] text-zinc-400 font-mono uppercase mb-2">Daily Messages</div>
              <div className="flex items-end gap-1 h-10">
                {dailyActivity.map(day => {
                  const height = Math.max((day.messages / maxMessages) * 100, 4);
                  return (
                    <div
                      key={day.date}
                      className="flex-1 bg-cyan-500/40 rounded-sm hover:bg-cyan-400/60 transition-colors"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${day.messages.toLocaleString()} msgs`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-1">
                {dailyActivity.length > 0 && (
                  <>
                    <span className="text-[10px] text-zinc-500 font-mono">
                      {dailyActivity[0].date.slice(5)}
                    </span>
                    {dailyActivity.length > 1 && (
                      <span className="text-[10px] text-zinc-500 font-mono">
                        {dailyActivity[dailyActivity.length - 1].date.slice(5)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Footer stats */}
          <div className="pt-4 border-t border-white/5 flex justify-between text-[10px] font-mono">
            <div>
              <span className="text-zinc-400">SESSIONS </span>
              <span className="text-zinc-200 tabular-nums">{data?.summary?.totalSessions ?? 0}</span>
            </div>
            <div>
              <span className="text-zinc-400">MSGS </span>
              <span className="text-zinc-200 tabular-nums">{(data?.summary?.totalMessages ?? 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-zinc-400">TOOLS </span>
              <span className="text-zinc-200 tabular-nums">{(data?.totalToolCalls ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Freshness indicator */}
          {data?.summary?.lastComputedDate && (
            <div className="mt-3 text-[10px] text-zinc-500 font-mono text-right">
              Last updated: {data.summary.lastComputedDate}
            </div>
          )}
        </>
      )}
    </div>
  );
};
